import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Crumbs, Empty } from "@/components/site/bits";
import { DealGrid } from "@/components/site/DealCard";
import { CouponRow } from "@/components/site/StoreCoupons";

export default function FestivePage() {
  const [store, setStore] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["festive", "diwali"], queryFn: () => api.get("/public/festive/diwali").then((r) => r.data) });
  useSeo({
    title: "Diwali Offers & Coupon Codes",
    description: "Live Diwali and festive coupon codes from trusted Indian stores, collected in one place with expiry dates. Updated from affiliate networks.",
  });
  const list = (data?.coupons || []).filter((c) => !store || c.merchant_name === store);
  return (
    <div data-testid="festive-page">
      <section className="bg-gradient-to-br from-amber-500 via-orange-500 to-rose-600 text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-xs uppercase tracking-widest font-bold flex items-center gap-2"><Sparkles size={14} /> Festive offers hub</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold mt-2">Diwali offers</h1>
          <p className="mt-3 max-w-2xl text-white/90">
            Every live Diwali and festive code from our trusted-store list, soonest-ending first. Offers come straight from the stores' affiliate network and change often, so always check the discount at checkout.
          </p>
          {data && <p className="mt-4 font-bold" data-testid="festive-count">{data.coupons.length} live offers · {data.stores.length} stores</p>}
        </div>
      </section>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <Crumbs items={[["/", "Home"], ["/coupons", "Coupons"], [null, "Diwali offers"]]} />
        {data?.stores?.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="festive-store-filter">
            <button onClick={() => setStore("")} data-testid="festive-store-all" className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${!store ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>All stores</button>
            {data.stores.map((s) => (
              <button key={s} onClick={() => setStore(s)} data-testid={`festive-store-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${store === s ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>{s}</button>
            ))}
          </div>
        )}
        {isLoading ? <div className="h-40 rounded-xl bg-slate-200/60 animate-pulse" /> : list.length ? (
          <ul className="space-y-3" data-testid="festive-coupons">{list.map((c) => <CouponRow key={c.id} c={c} showStore />)}</ul>
        ) : <Empty text="No live Diwali offers from trusted stores right now. New offers are checked with every import." testid="festive-empty" />}
        {data?.deals?.length > 0 && (
          <section><h2 className="text-2xl font-bold mb-4">Festive product deals</h2><DealGrid deals={data.deals} testid="festive-deals-grid" /></section>
        )}
        <p className="text-xs text-slate-500">We may earn a commission when you shop through these links, at no extra cost to you. It never decides which offers appear here.</p>
      </div>
    </div>
  );
}
